import React, { useEffect, useRef } from 'react';
import { IAgenda, IAppointment, IAgendasConfigParams, IPatient, AgendaScheduleTypes, OutpatientsVisualizationMode, AgendasTypes } from '../../../../constants/types';
import usePageVisibility, { SnackbarType, useSnackBarState } from '../../../../hooks';
import { cancelAppointmentService, getAppoinmentsDateService, updateAppoinmentsService } from '../../../../services/agenda';
import { AppointmentsDate } from './AppointmentsDate';
import MultiAgenda from './MultiAgenda';
import { errorCodesCreateAppointment } from '../../../../utils/agenda';
import { IRequest, RequestStatus } from '../../Request/types';
import { fetchPatientsAction } from '../../../../redux/actions/patientsActions';
import { useDispatch } from 'react-redux';

interface AppointmentsProps {
    uuidInvestigation: string;
    patientsPersonalData: IPatient[];
    uuidAgendas: string[];
    extraForm?: number;
    typeAgenda:AgendasTypes;
    agendas:IAgenda[];
    dateSelected: Date;
    canCreateAppointments: boolean;
    request:IRequest | null;
    appointment:IAppointment | null;
    type: AgendaScheduleTypes;
    mode:OutpatientsVisualizationMode,
    shouldReloadOnTabChange:boolean,
    preSelectedPatientUuid?: string;
    callbackAppointments?: (appointments:IAppointment[]) => void;
}

const Appointments: React.FC<AppointmentsProps> = ({ uuidInvestigation, mode, uuidAgendas, dateSelected,agendas, canCreateAppointments,
                                                    typeAgenda, type, shouldReloadOnTabChange, patientsPersonalData, appointment, extraForm, callbackAppointments, request, preSelectedPatientUuid }) => {
    const [loadingAppointments, setLoadingAppointments] = React.useState(false);
    const [appointments, setAppointments] = React.useState<IAppointment[]>([]);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();
    const [loadingSingleAppointment, setLoadingSingleAppointment] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(new Date());
    const [showModal, setShowModal] = React.useState(false);
    const dispatch = useDispatch();

    const prevUuidAgendas = useRef<string[] | undefined>();
    const prevDateSelected = useRef<Date | undefined>();

    function updateAppointment(uuidAppointment:string){
        setLoadingSingleAppointment(true);
        updateAppoinmentsService(uuidInvestigation, uuidAppointment)
            .then(response => {
                const indexAppointment = appointments.findIndex((appointment) => appointment.uuid === uuidAppointment);
                if(indexAppointment !== -1){
                    appointments[indexAppointment] = response.appointment;
                }
                setShowSnackbar({show:true, message:"pages.hospital.outpatients.checkin_success", severity:"success"});
                setLoadingSingleAppointment(false);
                resetModal();
                setLastUpdate(new Date());
            })
            .catch(err => {
                let message = "general.error";
                if(err.response.status === 401){
                    message = "pages.hospital.outpatients.no_permissions";
                }
                else if(err.response.data.errorCode === 1){
                    message = "pages.hospital.outpatients.error_ready_surgery";
                }
                else if(err.response.data.errorCode === "PATIENT_NOT_PRICE"){
                    message = "pages.hospital.outpatients.appointment.error.patient_not_insurance";
                }
                
                setShowSnackbar({show:true, message:message, severity:"error"});
                setLoadingSingleAppointment(false);
            })
    }
    function resetModal(){
        setShowModal(false);
    }
    function callbackResetModal(){
        resetModal();
    }
    function cancelAppointment(uuidAppointment:string, byUser:boolean = false){
        setLoadingAppointments(true);
        cancelAppointmentService(uuidInvestigation, uuidAppointment, byUser)
            .then(response => {
                const indexAppointment = appointments.findIndex((appointment) => appointment.uuid === uuidAppointment);
                if(!byUser){
                    appointments.splice(indexAppointment, 1);
                }
                else{
                    appointments[indexAppointment].requestAppointment.status = RequestStatus.CANCELED_BY_USER;
                }
                setShowSnackbar({show:true, message:"pages.hospital.outpatients.cancel_success", severity:"success"});
                setLoadingAppointments(false);
                setLastUpdate(new Date());
            })
            .catch(err => {
                setShowSnackbar({show:true, message:"general.error", severity:"error"});
                setLoadingAppointments(false);
                setLastUpdate(new Date());
            })
    }

    function appointmentCreatedCallback(appointment:IAppointment){
        setAppointments([...appointments, appointment]);
        setShowSnackbar({show:true, message:"pages.hospital.outpatients.calendar.new_appointment.success", severity:"success"});
        setLastUpdate(new Date());
    }

    function appointmentErrorCallback(error:any){
        if(error.errorCode){
            setShowSnackbar({show:true, message:errorCodesCreateAppointment(error.errorCode), severity:"error"});    
        }
    }

    async function getAllAgendas(){
        let appointmentsNew: IAppointment[] = [];
        for(let i = 0; i < uuidAgendas.length; i++){
            const uuidAgenda = uuidAgendas[i];
            await getAppoinmentsDate(uuidAgenda, dateSelected, appointmentsNew)
        }
        setLastUpdate(new Date());
        setAppointments(appointmentsNew);
    }

    useEffect(() => {
        // Debug: log which dependency is changing
        if (prevUuidAgendas.current !== uuidAgendas) {
            console.log("uuidAgendas changed:", uuidAgendas);
        }
        if (prevDateSelected.current !== dateSelected) {
            console.log("dateSelected changed:", dateSelected);
        }
        prevUuidAgendas.current = uuidAgendas;
        prevDateSelected.current = dateSelected;
        if (uuidAgendas.length > 0 && dateSelected) {
            getAllAgendas();
        }
    }, [uuidAgendas, dateSelected]);

    useEffect(() => {
        if (appointment) {
            const existingIndex = appointments.findIndex(a => a.uuid === appointment.uuid);
            if (existingIndex === -1) {
                setAppointments([...appointments, appointment]);
            }
        }
    }, [appointment]);


    usePageVisibility(() => {
        const fetchData = async () => {
          try {
            await dispatch(fetchPatientsAction(uuidInvestigation, true));
          } catch (error) {
            console.error('Error fetching patients:', error);
          }
        };
        
        if(uuidAgendas && dateSelected && shouldReloadOnTabChange){
            getAllAgendas();
        }
        if(shouldReloadOnTabChange){
            fetchData();
        }
      });

    async function getAppoinmentsDate(uuidAgendas:string, date:Date, appointmentsExt:IAppointment[]){
        setLoadingAppointments(true);
        try {
            const response = await getAppoinmentsDateService(uuidInvestigation, uuidAgendas, date);
            appointmentsExt.push(...response.appointments); // Modify appointmentsExt directly
            if (callbackAppointments) {
                callbackAppointments(response.appointments);
            }
        } catch (err) {
            // Handle error if needed
        } finally {
            setLoadingAppointments(false);
        }
    }
    
    if(type === AgendaScheduleTypes.DATE_TIME){
        const filteredAgendas = agendas.filter(agenda => uuidAgendas.includes(agenda.uuid));
        const uuidPatients = appointments.map(appointment => appointment.patient.uuid);
        const filteredPatients = patientsPersonalData.filter(patient => uuidPatients.includes(patient.uuid));
        console.log('filtered,kall')
        return <MultiAgenda key={`${dateSelected.getUTCMilliseconds()}-${appointments.length}`} date={dateSelected} agendas={filteredAgendas} appointments={appointments}
                    patients={filteredPatients} showSnackbar={showSnackbar} lastUpdate={lastUpdate.getTime()}
                    uuidInvestigation={uuidInvestigation}
                    typeAgenda={typeAgenda} request={request}
                    appointment={appointment}
                    extraForm={extraForm} appointmentCreatedCallback={appointmentCreatedCallback}
                    appointmentErrorCallback={appointmentErrorCallback} canCreateAppointments={canCreateAppointments}
                    preSelectedPatientUuid={preSelectedPatientUuid}
                    callbackSetSnackbar={(showSnackbar:SnackbarType) => setShowSnackbar(showSnackbar)}
                    cancelCallback={cancelAppointment} showUpCallback={updateAppointment} />
    }
    return (
        <AppointmentsDate loadingAppointments={loadingAppointments} appointments={appointments} 
            loadingSingleAppointment={loadingSingleAppointment} showSnackbar={showSnackbar}
            typeAgenda={typeAgenda} dateSelected={dateSelected}
            callbackResetModal={callbackResetModal} mode={mode} patientsPersonalData={patientsPersonalData}
            callbackSetSnackbar={(showSnackbar:SnackbarType) => setShowSnackbar(showSnackbar)}
            callbackCancelAppointment={(uuidAppointment) => cancelAppointment(uuidAppointment)}
            callbackUpdateAppointment={(uuidAppointment) => updateAppointment(uuidAppointment)}
            />
    );
};

export default Appointments;
